import { call, put, select, takeLatest, all } from 'redux-saga/effects';
import * as actions from './actions';
import { getSettingsMetaDataAsync } from 'store/settings/sagas';
import { getSupportedEventTypesAsync } from 'store/event/sagas';
import { createSupportedEventTypeOptions } from 'store/event/helpers';

// Selectors
const currentTenantId = store => store.user.selectedTenant.tenantId;

// Saga to get context data for copy modal (settings + supported event types)
export function* getCopyModalContextDataIfNeeded(action) {
  try {
    const { businessContextId } = action.payload;

    yield put(actions.gettingCopyModalContextData());

    const tenantId = yield select(currentTenantId);
    
    // Call both APIs in parallel - reusing existing API functions
    const { settingsMetaData, supportedEventTypesData } = yield all({
      settingsMetaData: call(getSettingsMetaDataAsync, tenantId, businessContextId),
      supportedEventTypesData: call(getSupportedEventTypesAsync, tenantId, businessContextId)
    });

    // Process the supported event types the same way as the main store
    const processedEventTypes = createSupportedEventTypeOptions(supportedEventTypesData, settingsMetaData);

    const supportedEventTypes = {
      data: supportedEventTypesData,
      options: processedEventTypes.eventOptions,
      productIntegrationOptions: processedEventTypes.productIntegrationOptions,
      allEventTypeCategoryOptions: processedEventTypes.allEventTypeCategoryOptions,
      allEventOptions: processedEventTypes.allEventOptions,
      internalSurveyResponseOptions: processedEventTypes.internalSurveyResponseOptions,
      externalSurveyResponseOptions: processedEventTypes.externalSurveyResponseOptions,
      productCategories: processedEventTypes.availableIntegrations?.productCategories,
      productNames: processedEventTypes.availableIntegrations?.productNames
    };

    yield put(actions.gettingCopyModalContextDataSucceeded({
      settingsMetaData,
      supportedEventTypes,
      businessContextId
    }));

  } catch (error) {
    console.error('Error getting copy modal context data:', error);
    yield put(actions.gettingCopyModalContextDataFailed(error.message || 'Failed to get context data'));
  }
}

// Saga to get only supported event types for copy modal
export function* getSupportedEventTypesIfNeeded(action) {
  try {
    const { businessContextId } = action.payload;

    yield put(actions.gettingSupportedEventTypes());

    const tenantId = yield select(currentTenantId);
    
    // Get settings first (might be needed for processing)
    const settingsMetaData = yield call(getSettingsMetaDataAsync, tenantId, businessContextId);
    const supportedEventTypesData = yield call(getSupportedEventTypesAsync, tenantId, businessContextId);

    // Process the supported event types the same way as the main store
    const processedEventTypes = createSupportedEventTypeOptions(supportedEventTypesData, settingsMetaData);

    const supportedEventTypes = {
      data: supportedEventTypesData,
      options: processedEventTypes.eventOptions,
      productIntegrationOptions: processedEventTypes.productIntegrationOptions,
      allEventTypeCategoryOptions: processedEventTypes.allEventTypeCategoryOptions,
      allEventOptions: processedEventTypes.allEventOptions,
      internalSurveyResponseOptions: processedEventTypes.internalSurveyResponseOptions,
      externalSurveyResponseOptions: processedEventTypes.externalSurveyResponseOptions,
      productCategories: processedEventTypes.availableIntegrations?.productCategories,
      productNames: processedEventTypes.availableIntegrations?.productNames,
      businessContextId
    };

    yield put(actions.gettingSupportedEventTypesSucceeded(supportedEventTypes));

  } catch (error) {
    console.error('Error getting supported event types:', error);
    yield put(actions.gettingSupportedEventTypesFailed(error.message || 'Failed to get supported event types'));
  }
}

// Watcher sagas
export function* watchGetCopyModalContextData() {
  yield takeLatest(actions.GET_COPY_MODAL_CONTEXT_DATA, getCopyModalContextDataIfNeeded);
}

export function* watchGetSupportedEventTypes() {
  yield takeLatest(actions.GET_COPY_MODAL_SUPPORTED_EVENT_TYPES, getSupportedEventTypesIfNeeded);
}

// Root saga
export default function* copyTemplateModalSaga() {
  yield all([
    watchGetCopyModalContextData(),
    watchGetSupportedEventTypes()
  ]);
}